import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';

const Canvas = ({ layers, selectedLayerId, canvasRef, backgroundColor, onUpdateLayer }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [draggedLayerId, setDraggedLayerId] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [resizeOffset, setResizeOffset] = useState({ x: 0, y: 0 });
    const [resizeHandle, setResizeHandle] = useState(null);
    const [isRotating, setIsRotating] = useState(false);
    const [rotateStartAngle, setRotateStartAngle] = useState(0);
    const [previewLayer, setPreviewLayer] = useState(null);
    const [cursor, setCursor] = useState('default');

    const handleMouseMove = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        let newCursor = 'default';

        const hoveredLayer = layers
            .slice()
            .reverse()
            .find(layer => {
                if (layer.type === 'image') {
                    return mouseX >= layer.x && mouseX <= layer.x + layer.width &&
                        mouseY >= layer.y && mouseY <= layer.y + layer.height;
                }
                if (layer.type === 'text') {
                    const textWidth = canvas.getContext('2d').measureText(layer.text).width;
                    const textHeight = layer.fontSize;
                    return mouseX >= layer.x && mouseX <= layer.x + textWidth &&
                       mouseY >= layer.y - textHeight && mouseY <= layer.y;
                }
                return false;
            });
        if (hoveredLayer) {
            const handleSize = 8;
            const newPreviewLayer = { ...hoveredLayer };
            if (
                mouseX >= newPreviewLayer.x + newPreviewLayer.width - handleSize &&
                mouseX <= newPreviewLayer.x + newPreviewLayer.width + handleSize &&
                mouseY >= newPreviewLayer.y + newPreviewLayer.height - handleSize &&
                mouseY <= newPreviewLayer.y + newPreviewLayer.height + handleSize
            ) {
                newCursor = 'nwse-resize';
            } else {
                const dx = mouseX - (newPreviewLayer.x + newPreviewLayer.width / 2);
                const dy = mouseY - (newPreviewLayer.y);
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance <= handleSize + 2) {
                    newCursor = 'grab';
                }
            }
            setPreviewLayer(newPreviewLayer);
        } else {
            setPreviewLayer(null);
        }

        if (previewLayer) {
            const handleSize = 8;
            if (
                mouseX >= previewLayer.x + previewLayer.width - handleSize &&
                mouseX <= previewLayer.x + previewLayer.width + handleSize &&
                mouseY >= previewLayer.y + previewLayer.height - handleSize &&
                mouseY <= previewLayer.y + previewLayer.height + handleSize
            ) {
                newCursor = 'nwse-resize';
            } else {
                const dx = mouseX - (previewLayer.x + previewLayer.width / 2);
                const dy = mouseY - (previewLayer.y);
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance <= handleSize + 2) {
                    newCursor = 'grab';
                }
            }
        }
        setCursor(newCursor);

        if (isRotating && draggedLayerId) {
            const draggedLayer = layers.find(layer => layer.id === draggedLayerId);
            if (!draggedLayer || !previewLayer) return;
            const angle = Math.atan2(mouseY - (draggedLayer.y), mouseX - (draggedLayer.x + draggedLayer.width / 2));
            const rotation = angle - rotateStartAngle;
             const updatedLayer = { ...previewLayer, rotation: rotation };
            setPreviewLayer(updatedLayer);
             onUpdateLayer(draggedLayerId, updatedLayer);
             return;
        }

       if (isResizing && draggedLayerId) {
            const draggedLayer = layers.find(layer => layer.id === draggedLayerId);
            if (!draggedLayer || !previewLayer) return;
            let newWidth = mouseX - draggedLayer.x - resizeOffset.x;
            let newHeight = mouseY - draggedLayer.y - resizeOffset.y;
             if (newWidth < 5) {
                 newWidth = 5;
            }
            if (newHeight < 5) {
                 newHeight = 5;
             }
            let ratio = newWidth / draggedLayer.width;
              if (event.shiftKey) {
                  newHeight = draggedLayer.height * ratio;
              }
              const updatedLayer = { ...previewLayer, width: newWidth, height: newHeight };
            setPreviewLayer(updatedLayer);
              onUpdateLayer(draggedLayerId, updatedLayer);
              return;
        }
       if (!isDragging || !draggedLayerId) return;
        const draggedLayer = layers.find(layer => layer.id === draggedLayerId);
        if (draggedLayer && previewLayer) {
            const newX = mouseX - dragOffset.x;
            const newY = mouseY - dragOffset.y;
            const updatedLayer = { ...previewLayer, x: newX, y: newY };
            setPreviewLayer(updatedLayer);
             onUpdateLayer(draggedLayerId, updatedLayer);
        }
    };

    const handleMouseDown = (event) => {
         const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

         const clickedLayer = layers
            .slice()
            .reverse()
            .find(layer => {
                 if (layer.type === 'image') {
                    return mouseX >= layer.x && mouseX <= layer.x + layer.width &&
                        mouseY >= layer.y && mouseY <= layer.y + layer.height;
                }
                if (layer.type === 'text') {
                    const textWidth = canvas.getContext('2d').measureText(layer.text).width;
                    const textHeight = layer.fontSize;
                    return mouseX >= layer.x && mouseX <= layer.x + textWidth &&
                       mouseY >= layer.y - textHeight && mouseY <= layer.y;
                }
                 return false;
            });
         if (clickedLayer) {
            const handleSize = 8;

            if (
                mouseX >= clickedLayer.x + clickedLayer.width - handleSize &&
                mouseX <= clickedLayer.x + clickedLayer.width + handleSize &&
                mouseY >= clickedLayer.y + clickedLayer.height - handleSize &&
                mouseY <= clickedLayer.y + clickedLayer.height + handleSize
            ) {
                setIsResizing(true);
                 setResizeHandle('bottom-right');
                 setResizeOffset({ x: mouseX - clickedLayer.x - clickedLayer.width, y: mouseY - clickedLayer.y - clickedLayer.height });
                setDraggedLayerId(clickedLayer.id);
                setPreviewLayer(clickedLayer);
                return;
            }

            const dx = mouseX - (clickedLayer.x + clickedLayer.width / 2);
            const dy = mouseY - clickedLayer.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance <= handleSize + 2) {
                 setIsRotating(true);
                 setDraggedLayerId(clickedLayer.id);
                 const angle = Math.atan2(mouseY - (clickedLayer.y), mouseX - (clickedLayer.x + clickedLayer.width / 2));
                 setRotateStartAngle(angle);
                 setPreviewLayer(clickedLayer);
                 return;
            }
            setIsDragging(true);
            setDraggedLayerId(clickedLayer.id);
            setDragOffset({ x: mouseX - clickedLayer.x, y: mouseY - clickedLayer.y });
            setPreviewLayer(clickedLayer);
         }
    };


    const handleMouseUp = () => {
        setPreviewLayer(null);
        setIsDragging(false);
        setDraggedLayerId(null);
        setIsResizing(false);
        setResizeHandle(null);
        setIsRotating(false);
        setCursor('default');
    };


     useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        layers.forEach(layer => {
            let layerToDraw = layer;
           if (previewLayer && layer.id === previewLayer.id) {
                layerToDraw = { ...layer, ...previewLayer };
            }
             ctx.save();
            ctx.translate(layerToDraw.x + layerToDraw.width / 2, layerToDraw.y + layerToDraw.height / 2);
            if (layerToDraw.rotation) {
                ctx.rotate(layerToDraw.rotation);
            }
            ctx.translate(-layerToDraw.x - layerToDraw.width / 2, -layerToDraw.y - layerToDraw.height / 2);


            if (layerToDraw.type === 'image') {
                const image = new Image();
                image.onload = () => {
                    ctx.globalAlpha = layerToDraw.opacity;
                    ctx.drawImage(image, layerToDraw.x, layerToDraw.y, layerToDraw.width, layerToDraw.height);
                    ctx.globalAlpha = 1;
                };
                image.src = layerToDraw.src;
             }

             if (layerToDraw.type === 'text') {
                 ctx.font = `${layerToDraw.fontStyle} ${layerToDraw.fontSize}px ${layerToDraw.fontFamily}`;
                ctx.fillStyle = layerToDraw.color;
                ctx.globalAlpha = layerToDraw.opacity;

                 if (layerToDraw.style === 'bold') {
                     ctx.font = `bold ${layerToDraw.fontSize}px ${layerToDraw.fontFamily}`;
                }

                if (layerToDraw.style === 'border') {
                   ctx.strokeStyle = layerToDraw.borderColor;
                    ctx.lineWidth = layerToDraw.borderWidth;
                    ctx.strokeText(layerToDraw.text, layerToDraw.x, layerToDraw.y);
               }

               if (layerToDraw.style === 'shadow') {
                   ctx.shadowColor = layerToDraw.shadowColor;
                     ctx.shadowBlur = layerToDraw.shadowBlur;
                    ctx.shadowOffsetX = layerToDraw.shadowOffsetX;
                    ctx.shadowOffsetY = layerToDraw.shadowOffsetY;
                 }
                if (layerToDraw.style === 'neon') {
                   ctx.shadowColor = layerToDraw.color;
                     ctx.shadowBlur = 10;
               }

                ctx.fillText(layerToDraw.text, layerToDraw.x, layerToDraw.y);
                ctx.globalAlpha = 1;
            }
            ctx.restore();

             if (selectedLayerId === layer.id) {
                ctx.beginPath();
                ctx.rect(layer.x, layer.y, layer.width, layer.height);
                 ctx.strokeStyle = 'blue';
                ctx.lineWidth = 1;
                ctx.stroke();

                const handleSize = 8;
                 ctx.fillStyle = 'blue';
                ctx.fillRect(layer.x + layer.width - handleSize, layer.y + layer.height - handleSize, handleSize * 2, handleSize * 2);

                 ctx.beginPath();
                 ctx.arc(layer.x + layer.width / 2, layer.y, handleSize, 0, 2 * Math.PI, false);
                ctx.fillStyle = 'blue';
                ctx.fill();
            }
        });

    }, [layers, backgroundColor, selectedLayerId, previewLayer]);


    return (
        <Box
            position="relative"
            width="600px"
            height="400px"
            sx={{ cursor: isDragging ? 'grabbing' : cursor }}
        >
            <canvas
                ref={canvasRef}
                width={600}
                height={400}
                style={{ border: '1px solid #ccc', cursor: isDragging ? 'grabbing' : cursor }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            />
            {previewLayer && (
                <Box
                    position="absolute"
                    top={previewLayer.y}
                    left={previewLayer.x}
                    width={previewLayer.width}
                    height={previewLayer.height}
                    style={{
                        pointerEvents: 'none',
                        transform: `rotate(${previewLayer.rotation || 0}rad)`,
                        transformOrigin: `${previewLayer.width / 2}px ${previewLayer.height / 2}px`
                    }}
                >
                     <Box
                        position="absolute"
                        top={previewLayer.height - 8}
                        left={previewLayer.width - 8}
                        width={16}
                        height={16}
                        style={{
                            cursor: 'nwse-resize',
                            backgroundColor: 'blue',
                            border: '1px solid #fff',
                        }}
                    />
                    <Box
                        position="absolute"
                        top={-8}
                        left={previewLayer.width / 2 - 8}
                        width={16}
                        height={16}
                       style={{
                           cursor: 'grab',
                            backgroundColor: 'blue',
                            border: '1px solid #fff',
                           borderRadius: '50%',
                        }}
                    />
                    {previewLayer.type === 'image' && (
                        <img
                            src={previewLayer.src}
                            style={{ opacity: previewLayer.opacity, width: '100%', height: '100%', objectFit: 'fill' }}
                            alt=""
                        />
                    )}
                    {previewLayer.type === 'text' && (
                        <div
                             style={{
                                opacity: previewLayer.opacity,
                                fontFamily: previewLayer.fontFamily,
                                fontSize: `${previewLayer.fontSize}px`,
                                color: previewLayer.color,
                                fontWeight: previewLayer.style === 'bold' ? 'bold' : 'normal',
                                position: 'relative',
                            }}
                        >
                            {previewLayer.style === 'border' &&
                                <span style={{
                                    WebkitTextStroke: `${previewLayer.borderWidth}px ${previewLayer.borderColor}`,
                                        color: previewLayer.color,
                                }}>
                                    {previewLayer.text}
                                </span>
                            }
                            {previewLayer.style === 'shadow' &&
                                <span
                                    style={{
                                      textShadow: `${previewLayer.shadowOffsetX}px ${previewLayer.shadowOffsetY}px ${previewLayer.shadowBlur}px ${previewLayer.shadowColor}`,
                                      color: previewLayer.color,
                                   }}
                                 >
                                    {previewLayer.text}
                                </span>
                           }
                            {previewLayer.style === 'neon' &&
                                <span
                                    style={{
                                      textShadow: `0px 0px 10px ${previewLayer.color}`,
                                      color: previewLayer.color,
                                  }}
                                 >
                                    {previewLayer.text}
                                </span>
                            }
                           {(previewLayer.style !== 'border' && previewLayer.style !== 'shadow' && previewLayer.style !== 'neon') &&
                              <span>{previewLayer.text}</span>
                            }

                        </div>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Canvas;